import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  CreditCard, 
  BookOpen, 
  FileText,
  Clock,
  Users
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuickAction {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  onClick: () => void;
}

interface CanvaQuickActionsProps {
  className?: string;
  onScheduleClick?: () => void;
  onBillingClick?: () => void;
  onEnrollmentClick?: () => void;
  onDocumentsClick?: () => void;
}

export const CanvaQuickActions: React.FC<CanvaQuickActionsProps> = ({ 
  className,
  onScheduleClick,
  onBillingClick,
  onEnrollmentClick,
  onDocumentsClick
}) => {
  const quickActions: QuickAction[] = [
    {
      title: "View Schedule",
      subtitle: "Check upcoming classes",
      icon: Calendar,
      colorClass: "canva-action-blue",
      onClick: onScheduleClick || (() => console.log('Navigate to schedule'))
    },
    {
      title: "Pay Bills",
      subtitle: "Manage payments",
      icon: CreditCard,
      colorClass: "canva-action-green",
      onClick: onBillingClick || (() => console.log('Navigate to billing'))
    },
    {
      title: "Enroll Classes",
      subtitle: "Browse programs",
      icon: BookOpen,
      colorClass: "canva-action-purple",
      onClick: onEnrollmentClick || (() => console.log('Navigate to enrollment'))
    },
    {
      title: "Documents",
      subtitle: "Forms & reports",
      icon: FileText,
      colorClass: "canva-action-orange",
      onClick: onDocumentsClick || (() => console.log('Navigate to documents'))
    }
  ];

  return (
    <div className={cn("mb-12", className)}>
      <motion.h2 
        className="text-3xl font-bold text-gray-900 mb-8 tracking-tight text-center"
        style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Quick Actions
      </motion.h2>
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {quickActions.map((action, index) => (
          <motion.button
            key={action.title}
            className={cn(
              "group relative overflow-hidden p-8 rounded-2xl font-semibold text-white shadow-xl hover:shadow-2xl transition-all duration-300",
              action.colorClass
            )}
            onClick={action.onClick}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            whileHover={{ scale: 1.03, y: -4 }}
            whileTap={{ scale: 0.97 }}
            style={{
              minHeight: '140px',
              background: action.colorClass === 'canva-action-blue' 
                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                : action.colorClass === 'canva-action-green'
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : action.colorClass === 'canva-action-purple'
                ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            }}
          >
            {/* Premium background decoration */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full transform translate-x-8 -translate-y-8" />
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full transform -translate-x-6 translate-y-6" />
            
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
              <div className="mb-4 group-hover:scale-110 transition-transform duration-300">
                <action.icon className="w-10 h-10 mx-auto text-white drop-shadow-lg" />
              </div>
              <p className="font-bold text-white mb-2 text-lg">{action.title}</p>
              <p className="text-sm text-white/90 leading-relaxed">{action.subtitle}</p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default CanvaQuickActions;